import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format, subDays } from 'date-fns';
import toast from 'react-hot-toast';
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  PaginatedTable,
  type PaginatedTableColumn,
  Alert,
  AlertTitle,
  AlertDescription,
} from '../../../../components/ui';
import { DashboardEmptyStateCard, DashboardEmptyStateIcon } from '@/components/dashboard/DashboardEmptyState';
import { DashboardDataCard } from '@/components/dashboard/DashboardSectionCard';
import { DashboardTabNav } from '@/components/dashboard/DashboardTabNav';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { useAuthStore } from '../../../../store/auth';
import {
  Booking,
  Lesson,
  Academy,
  Term,
  hasAcademyManagementRole,
  type PastActiveTermCleanupPreview,
} from '@court-sync/shared';
import type { User } from '@court-sync/shared';
import { bookingService } from '../../../../services/booking';
import { lessonService } from '../../../../services/lesson';
import { useSubscriptionCheck } from '../../../../hooks/useSubscriptionCheck';
import { useManagementDashboardFeatureAccess } from '../../../../hooks/useFeatureAccess';
import { useAcademyBookingsPage, useTerms } from '../../../../hooks/useDataStore';
import { FeatureGate } from '@/components/FeatureGate';
import { Student, StudentProfile, TierFeatureKey } from '@court-sync/shared';
import { useCurrentAcademy } from '../../../../hooks/useDataStore';
import { UserAvatar } from '@/components/UserAvatar';
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  BookMarked,
  BookOpen,
  Building2,
  CalendarDays,
  ChevronDown,
  ClipboardList,
  Contact,
  Download,
  MoreVertical,
  Clock,
  DollarSign,
  Filter,
  Search,
  Trash2,
  User as UserIcon,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageFeatureHelp } from '@/components/tour/PageFeatureHelp';
import { usePageTourActions } from '@/components/tour/PageTourHost';
import { BOOKINGS_MANAGEMENT_TOUR_STEP_IDS } from '@/lib/tour-constants';
import {
  BOOKINGS_LIST_TOUR_KEY,
  getBookingsTabHelp,
  resolveBookingsTabTourKey,
  scheduleBookingsListTourStart,
} from '@/lib/tours/bookingsManagementTour';
import { useAutoPageTour } from '@/hooks/useAutoPageTour';
import { TierUnlockBadge } from '@/components/tier/TierUnlockBadge';
import { getBookingsTabTierUnlockLevel } from '@/lib/tierUnlockBadges';
import { ExpressionsOfInterestPanel } from './ExpressionsOfInterestPanel';
import { PrivateLessonRequestsPanel } from './PrivateLessonRequestsPanel';
import { refundRequestService, type RefundRequestEnriched } from '../../../../services/refundRequest';
import { waitlistService } from '../../../../services/waitlist';
import type { WaitlistEntry } from '@court-sync/shared';
import { downloadCSV } from '../../../../utils/csvUtils';
import { buildBookingChargesCsvRows } from '../../../../utils/bookingChargesCsvExport';
import { BookingActionsMenu } from '@/components/bookings/BookingActionsMenu';
import { CancelBookingConfirmDialog } from '@/components/bookings/CancelBookingConfirmDialog';
import {
  buildCampSessionClustersWithVisibility,
  getBookingClusterActionIds,
  isCampDailySessionBooking,
} from '@/utils/campBookingClusters';

const DAYS_OF_WEEK = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
];

const BOOKING_PAGE_SIZE = 20;

const EXPORT_BOOKING_REPORT_NO_TERM = '__export_no_term__';

type BookingScopeFilterFieldsProps = {
  selectedTermFilter: string;
  onTermFilterChange: (value: string) => void;
  selectedLessonFilter: string;
  onLessonFilterChange: (value: string) => void;
  terms: Term[];
  lessons: Lesson[];
  lessonsLoading: boolean;
  idPrefix?: string;
};

function BookingScopeFilterFields({
  selectedTermFilter,
  onTermFilterChange,
  selectedLessonFilter,
  onLessonFilterChange,
  terms,
  lessons,
  lessonsLoading,
  idPrefix = 'booking-filter',
}: BookingScopeFilterFieldsProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label
          htmlFor={`${idPrefix}-term`}
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          Term
        </label>
        <select
          id={`${idPrefix}-term`}
          value={selectedTermFilter}
          onChange={(e) => onTermFilterChange(e.target.value)}
          className="w-full input"
        >
          <option value="all">All terms</option>
          {terms.map((term) => (
            <option key={term.id} value={term.id}>
              {term.name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label
          htmlFor={`${idPrefix}-lesson`}
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          Lesson
        </label>
        <select
          id={`${idPrefix}-lesson`}
          value={selectedLessonFilter}
          onChange={(e) => onLessonFilterChange(e.target.value)}
          disabled={selectedTermFilter === 'all' || lessonsLoading}
          className="w-full input disabled:opacity-60"
        >
          <option value="all">
            {selectedTermFilter === 'all'
              ? 'Select a term to filter by lesson'
              : lessonsLoading
                ? 'Loading lessons…'
                : 'All lessons in this term'}
          </option>
          {lessons.map((lesson) => (
            <option key={lesson.id} value={lesson.id}>
              {lesson.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function lessonTypeByIdFromMap(lessonById: { [key: string]: Lesson }): Record<string, string | undefined> {
  const out: Record<string, string | undefined> = {};
  for (const [id, lesson] of Object.entries(lessonById)) {
    out[id] = lesson?.lessonType;
  }
  return out;
}

function canExportAcademyBookingCharges(
  user: User | null | undefined,
  academy: Academy | null | undefined
): boolean {
  if (!user || !academy) return false;
  if (user.roles?.includes('admin')) return true;
  if (academy.ownerId === user.id) return true;
  return user.rolesWithAcademy?.some((r) => r.role === 'academy_admin' && r.academyId === academy.id) ?? false;
}

/** Session dates (YYYY-MM-DD) for schedule display; null = use non–daily camp branches. */
function getCampSessionDatesForDisplay(
  lesson: Lesson | undefined,
  booking: Booking,
  cluster: Booking[] | undefined
): string[] | null {
  if (!lesson || lesson.lessonType !== 'camp') return null;
  if (booking.sessionDate && (booking.campBookingType === 'daily' || lesson.campDayPrice != null)) {
    if (cluster && cluster.length > 1) {
      return [...new Set(cluster.map((b) => b.sessionDate).filter(Boolean) as string[])].sort();
    }
    return booking.sessionDate ? [booking.sessionDate] : [];
  }
  return null;
}

function renderBookingScheduleContent(
  lesson: Lesson,
  booking: Booking,
  sessionDatesForDisplay: string[] | null
): React.ReactNode {
  if (lesson.lessonType === 'camp') {
    const sessionDates = sessionDatesForDisplay ?? [];
    if (sessionDates.length > 0) {
      const timeStr = `${lesson.startTime}–${lesson.endTime}`;
      return (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {sessionDates.map((ymd) => (
              <span
                key={ymd}
                className="inline-flex rounded-md border border-primary/20 bg-primary/5 px-2 py-0.5 text-xs font-medium text-foreground dark:bg-primary/10 dark:border-primary/30"
              >
                {format(new Date(`${ymd}T12:00:00`), 'EEE d MMM yyyy')}
              </span>
            ))}
          </div>
          <p className="text-xs text-muted-foreground tabular-nums">{timeStr}</p>
        </div>
      );
    }
    if (
      booking.campBookingType === 'full_camp' ||
      (!booking.sessionDate && booking.campBookingType !== 'daily')
    ) {
      return (
        <div className="space-y-1">
          <p className="text-sm font-medium leading-snug text-foreground">
            {DAYS_OF_WEEK[lesson.daysOfWeek?.[0] ?? 0]}s · {lesson.startTime}–{lesson.endTime}
          </p>
          <p className="text-xs text-muted-foreground">Full camp</p>
        </div>
      );
    }
    return (
      <p className="text-sm font-medium leading-snug text-foreground">
        {DAYS_OF_WEEK[lesson.daysOfWeek?.[0] ?? 0]}s {lesson.startTime}–{lesson.endTime}
      </p>
    );
  }
  return (
    <p className="text-sm font-medium leading-snug text-foreground">
      {DAYS_OF_WEEK[lesson.daysOfWeek?.[0] ?? 0]}s {lesson.startTime}–{lesson.endTime}
    </p>
  );
}

function formatBookingCustomerName(booking: Booking): string | null {
  const parts = [booking.customerFirstName, booking.customerLastName].filter(
    (p): p is string => typeof p === 'string' && p.trim().length > 0
  );
  const joined = parts.join(' ').trim();
  return joined.length > 0 ? joined : null;
}

function BookingDetailDialogRow({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-3 py-3.5">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
        <div className="mt-1.5 text-sm text-foreground">{children}</div>
      </div>
    </div>
  );
}

type BookingEnrichedRow = {
  booking: Booking;
  lesson: Lesson | undefined;
  academy: Academy | undefined;
  lessonTitle: string;
  programName: string | null;
  studentName: string;
  student: StudentProfile | Student | undefined;
  campSessionCluster: Booking[] | undefined;
  pendingInCluster: string[];
  confirmedInCluster: string[];
  completedInCluster: string[];
  cancellableIds: string[];
};

export default function BookingManagement() {
  const { user } = useAuthStore();
  const { currentAcademy, userAcademies } = useCurrentAcademy();
  const navigate = useNavigate();
  const { hasValidSubscription, tooltipMessage } = useSubscriptionCheck();
  const { hasAccess: hasPaymentProcessingAccess } = useManagementDashboardFeatureAccess(TierFeatureKey.PAYMENT_PROCESSING);
  const {
    hasAccess: hasRebookingRefundsAccess,
    tierResolved: rebookingRefundsTierResolved,
  } = useManagementDashboardFeatureAccess(TierFeatureKey.REBOOKING_REFUNDS);
  const {
    hasAccess: hasWaitlistsAccess,
    tierResolved: waitlistsTierResolved,
  } = useManagementDashboardFeatureAccess(TierFeatureKey.WAITLISTS);

  const academyHasAutomaticPaymentProcessing = React.useMemo(
    () =>
      hasPaymentProcessingAccess &&
      !!(currentAcademy?.stripeConnectAccountId && currentAcademy?.onboardingComplete),
    [
      hasPaymentProcessingAccess,
      currentAcademy?.stripeConnectAccountId,
      currentAcademy?.onboardingComplete,
    ]
  );

  const [bookingPage, setBookingPage] = useState(1);
  const [extraLessonById, setExtraLessonById] = useState<Record<string, Lesson>>({});
  const [lessonsForTermPicker, setLessonsForTermPicker] = useState<Lesson[]>([]);
  const [lessonsForTermLoading, setLessonsForTermLoading] = useState(false);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>('all');
  const [selectedPaymentStatusFilter, setSelectedPaymentStatusFilter] = useState<string>('all');
  const [selectedStudentFilter, setSelectedStudentFilter] = useState<string>('all');
  const [selectedLessonFilter, setSelectedLessonFilter] = useState<string>('all');
  const [selectedTermFilter, setSelectedTermFilter] = useState<string>('all');
  const [studentNameSearch, setStudentNameSearch] = useState('');
  const [sortBy, setSortBy] = useState<'term-priority' | 'date' | 'student' | 'lesson' | 'amount' | 'status'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedBookingIds, setSelectedBookingIds] = useState<Set<string>>(() => new Set());
  const [detailBookingId, setDetailBookingId] = useState<string | null>(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [cancelConfirmIds, setCancelConfirmIds] = useState<string[]>([]);
  const [cancelConfirmSubmitting, setCancelConfirmSubmitting] = useState(false);
  const [refundRequests, setRefundRequests] = useState<RefundRequestEnriched[]>([]);
  const [refundRequestsLoading, setRefundRequestsLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: 'bookings' | 'waitlist' | 'eoi' | 'private-requests' = (() => {
    const t = searchParams.get('tab');
    if (t === 'waitlist' || t === 'eoi' || t === 'private-requests') return t;
    return 'bookings';
  })();
  const setActiveTab = (tab: 'bookings' | 'waitlist' | 'eoi' | 'private-requests') => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === 'bookings') next.delete('tab');
        else next.set('tab', tab);
        return next;
      },
      { replace: true }
    );
  };
  const { startTourWithCompletionKey } = usePageTourActions();
  const activeBookingsTourKey = resolveBookingsTabTourKey(activeTab);
  const [waitlistEntries, setWaitlistEntries] = useState<WaitlistEntry[]>([]);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistRemovingId, setWaitlistRemovingId] = useState<string | null>(null);

  const [exportReportOpen, setExportReportOpen] = useState(false);
  const [exportFrom, setExportFrom] = useState(() => format(subDays(new Date(), 6), 'yyyy-MM-dd'));
  const [exportTo, setExportTo] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [exportDateMatch, setExportDateMatch] = useState<'booking_created' | 'payment_succeeded'>(
    'booking_created'
  );
  const [exportBookingStatus, setExportBookingStatus] = useState<string>('all');
  const [exportPaymentStatus, setExportPaymentStatus] = useState<string>('all');
  const [exportTermFilter, setExportTermFilter] = useState<string>('all');
  const [exportLessonFilter, setExportLessonFilter] = useState<string>('all');
  const [exportModalLessons, setExportModalLessons] = useState<Lesson[]>([]);
  const [exportModalLessonsLoading, setExportModalLessonsLoading] = useState(false);
  const [exportRunning, setExportRunning] = useState(false);

  const [pastTermsReminderPreview, setPastTermsReminderPreview] =
    useState<PastActiveTermCleanupPreview | null>(null);
  const [pastTermsReminderLoading, setPastTermsReminderLoading] = useState(false);
  const [pastTermsReminderSubmitting, setPastTermsReminderSubmitting] = useState(false);

  const { terms: academyTermsList, invalidate: invalidateTerms } = useTerms(currentAcademy?.id);

  const terms = useMemo(() => {
    const map: Record<string, Term> = {};
    academyTermsList.forEach((term) => {
      map[term.id] = term;
    });
    return map;
  }, [academyTermsList]);

  const academies = useMemo(() => {
    const map: Record<string, Academy> = {};
    userAcademies.forEach((academy) => {
      map[academy.id] = academy;
    });
    return map;
  }, [userAcademies]);

  const bookingListFilters = useMemo(
    () => ({
      ...(selectedStatusFilter !== 'all' ? { status: selectedStatusFilter } : {}),
      ...(selectedLessonFilter !== 'all' ? { lessonId: selectedLessonFilter } : {}),
      ...(selectedTermFilter !== 'all' ? { termId: selectedTermFilter } : {}),
    }),
    [selectedStatusFilter, selectedLessonFilter, selectedTermFilter]
  );

  const {
    bookings,
    pagination: bookingPagination,
    lessonById: pageLessonById,
    students,
    isLoading: bookingListLoading,
    refresh: refreshBookingsPage,
  } = useAcademyBookingsPage(currentAcademy?.id, bookingPage, bookingListFilters, {
    enabled: !!currentAcademy?.id && activeTab === 'bookings',
  });

  const bookingsListTourContext = useMemo(
    () => ({
      isStarterPlan: !hasPaymentProcessingAccess,
      hasBookings: bookings.length > 0,
    }),
    [bookings.length, hasPaymentProcessingAccess],
  );
  const bookingsListTourContextRef = useRef(bookingsListTourContext);
  bookingsListTourContextRef.current = bookingsListTourContext;

  const launchBookingsListTour = useCallback(() => {
    scheduleBookingsListTourStart(
      startTourWithCompletionKey,
      bookingsListTourContextRef.current,
    );
  }, [startTourWithCompletionKey]);

  const startBookingsTabTour = useCallback(
    (tourKey: typeof BOOKINGS_LIST_TOUR_KEY) => {
      if (tourKey === BOOKINGS_LIST_TOUR_KEY) {
        launchBookingsListTour();
      }
    },
    [launchBookingsListTour],
  );

  const isBookingsTabTourReady = Boolean(currentAcademy?.id && activeTab === 'bookings');

  useAutoPageTour(activeBookingsTourKey, isBookingsTabTourReady, {
    onStart: startBookingsTabTour,
  });

  const lessonById = useMemo(
    () => ({ ...pageLessonById, ...extraLessonById }),
    [pageLessonById, extraLessonById]
  );

  const fetchBookingsPage = useCallback(
    async (page: number) => {
      if (page !== bookingPage) {
        setBookingPage(page);
        return;
      }
      await refreshBookingsPage({ force: true });
    },
    [bookingPage, refreshBookingsPage]
  );

  useEffect(() => {
    setBookingPage(1);
  }, [selectedStatusFilter, selectedLessonFilter, selectedTermFilter, currentAcademy?.id]);

  useEffect(() => {
    if (!academyHasAutomaticPaymentProcessing && exportDateMatch === 'payment_succeeded') {
      setExportDateMatch('booking_created');
    }
  }, [academyHasAutomaticPaymentProcessing, exportDateMatch]);

  useEffect(() => {
    if (!academyHasAutomaticPaymentProcessing && exportPaymentStatus !== 'all') {
      setExportPaymentStatus('all');
    }
  }, [academyHasAutomaticPaymentProcessing, exportPaymentStatus]);

  useEffect(() => {
    if (!academyHasAutomaticPaymentProcessing && selectedPaymentStatusFilter !== 'all') {
      setSelectedPaymentStatusFilter('all');
    }
  }, [academyHasAutomaticPaymentProcessing, selectedPaymentStatusFilter]);

  useEffect(() => {
    setSelectedBookingIds(new Set());
  }, [
    bookingPagination?.page,
    selectedStatusFilter,
    selectedLessonFilter,
    selectedTermFilter,
    currentAcademy?.id,
    activeTab,
  ]);

  // Check if user has academy owner access
  useEffect(() => {
    if (!user || !hasAcademyManagementRole(user)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const termsForCurrentAcademy = React.useMemo(() => {
    if (!currentAcademy?.id) return [];
    return Object.values(terms)
      .filter((t) => t.academyId === currentAcademy.id)
      .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
  }, [terms, currentAcademy?.id]);

  /** Reset lesson when term changes */
  useEffect(() => {
    setSelectedLessonFilter('all');
  }, [selectedTermFilter]);

  /** Load all lessons for the selected term (dropdown + labels) */
  useEffect(() => {
    if (!currentAcademy?.id || selectedTermFilter === 'all') {
      setLessonsForTermPicker([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLessonsForTermLoading(true);
      try {
        const res = await lessonService.getLessons({
          academyId: currentAcademy.id,
          termId: selectedTermFilter,
        });
        if (cancelled) return;
        const list = res.data || [];
        setLessonsForTermPicker(list);
        setExtraLessonById((prev) => {
          const next = { ...prev };
          for (const l of list) {
            next[l.id] = l;
          }
          return next;
        });
      } catch {
        if (!cancelled) {
          setLessonsForTermPicker([]);
          toast.error('Failed to load lessons for this term');
        }
      } finally {
        if (!cancelled) setLessonsForTermLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentAcademy?.id, selectedTermFilter]);

  /** Server-driven: ended-but-active term(s); backend may send academy owner an in-app reminder (deduped ~72h). */
  useEffect(() => {
    if (!currentAcademy?.id || activeTab !== 'bookings') {
      setPastTermsReminderPreview(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setPastTermsReminderLoading(true);
      try {
        const res = await bookingService.getPastActiveTermsCleanupPreview(currentAcademy.id);
        if (cancelled) return;
        if (!res.success || !res.data?.terms?.length) {
          setPastTermsReminderPreview(null);
          return;
        }
        const sortedIds = [
          ...new Set(
            res.data.terms.flatMap((t) => t.termIds ?? [t.termId])
          ),
        ]
          .sort()
          .join(',');
        const dismissKey = `courtsync.bookings.pastTermsReminder.dismissedSession.${currentAcademy.id}.${sortedIds}`;
        if (sessionStorage.getItem(dismissKey)) {
          setPastTermsReminderPreview(null);
          return;
        }
        setPastTermsReminderPreview(res.data);
      } catch {
        if (!cancelled) setPastTermsReminderPreview(null);
      } finally {
        if (!cancelled) setPastTermsReminderLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentAcademy?.id, activeTab]);

  const dismissPastTermsReminder = useCallback(() => {
    if (!pastTermsReminderPreview || !currentAcademy?.id) {
      setPastTermsReminderPreview(null);
      return;
    }
    const sortedIds = [
      ...new Set(
        pastTermsReminderPreview.terms.flatMap((t) => t.termIds ?? [t.termId])
      ),
    ]
      .sort()
      .join(',');
    sessionStorage.setItem(
      `courtsync.bookings.pastTermsReminder.dismissedSession.${currentAcademy.id}.${sortedIds}`,
      '1'
    );
    setPastTermsReminderPreview(null);
  }, [pastTermsReminderPreview, currentAcademy?.id]);

  const formatReminderMoney = useCallback(
    (amount: number) =>
      new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: String(currentAcademy?.currency ?? 'USD'),
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount),
    [currentAcademy?.currency]
  );

  const confirmPastTermsReminder = useCallback(async () => {
    if (!pastTermsReminderPreview?.terms?.length || !currentAcademy?.id) return;
    setPastTermsReminderSubmitting(true);
    try {
      const termIds = [
        ...new Set(
          pastTermsReminderPreview.terms.flatMap((t) => t.termIds ?? [t.termId])
        ),
      ];
      const res = await bookingService.completeEndedTermBookingsAndDeactivateTerm({
        academyId: currentAcademy.id,
        termIds,
      });
      if (!res.success) {
        toast.error((res as { error?: string }).error || 'Could not update terms');
        return;
      }
      toast.success(res.message || 'Bookings updated and term(s) set to inactive');
      invalidateTerms();
      setPastTermsReminderPreview(null);
      await fetchBookingsPage(bookingPagination?.page ?? 1);
    } catch (err: unknown) {
      const message =
        typeof err === 'object' && err !== null && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      toast.error(message || 'Could not update terms');
    } finally {
      setPastTermsReminderSubmitting(false);
    }
  }, [pastTermsReminderPreview, currentAcademy?.id, bookingPagination?.page, fetchBookingsPage, invalidateTerms]);

  const canExportCharges = React.useMemo(
    () => canExportAcademyBookingCharges(user, currentAcademy ?? undefined),
    [user, currentAcademy]
  );

  const openExportReportDialog = useCallback(() => {
    setExportFrom(format(subDays(new Date(), 6), 'yyyy-MM-dd'));
    setExportTo(format(new Date(), 'yyyy-MM-dd'));
    setExportDateMatch('booking_created');
    setExportBookingStatus('all');
    setExportPaymentStatus('all');
    setExportTermFilter('all');
    setExportLessonFilter('all');
    setExportReportOpen(true);
  }, []);

  useEffect(() => {
    if (!exportReportOpen || !currentAcademy?.id) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setExportModalLessonsLoading(true);
      try {
        let list: Lesson[] = [];
        if (exportTermFilter === 'all') {
          const res = await lessonService.getLessons({ academyId: currentAcademy.id });
          list = res.data ?? [];
        } else if (exportTermFilter === EXPORT_BOOKING_REPORT_NO_TERM) {
          const res = await lessonService.getLessons({ academyId: currentAcademy.id });
          list = (res.data ?? []).filter((l) => !l.termId);
        } else {
          const res = await lessonService.getLessons({
            academyId: currentAcademy.id,
            termId: exportTermFilter,
          });
          list = res.data ?? [];
        }
        if (!cancelled) {
          setExportModalLessons(list);
        }
      } catch {
        if (!cancelled) {
          setExportModalLessons([]);
          toast.error('Failed to load lessons for export');
        }
      } finally {
        if (!cancelled) {
          setExportModalLessonsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [exportReportOpen, currentAcademy?.id, exportTermFilter]);

  useEffect(() => {
    setExportLessonFilter('all');
  }, [exportTermFilter]);

  const handleRunExportReport = useCallback(async () => {
    if (!currentAcademy?.id || !canExportCharges) return;
    setExportRunning(true);
    try {
      const dateBasis =
        academyHasAutomaticPaymentProcessing && exportDateMatch === 'payment_succeeded'
          ? 'payment_succeeded'
          : 'booking_created';

      const params: Parameters<typeof bookingService.exportAcademyBookingCharges>[1] = {
        from: exportFrom,
        to: exportTo,
        dateBasis,
      };
      if (exportBookingStatus !== 'all') {
        params.status = exportBookingStatus as 'pending' | 'confirmed' | 'cancelled' | 'completed';
      }
      if (exportLessonFilter !== 'all') params.lessonId = exportLessonFilter;
      if (exportTermFilter === EXPORT_BOOKING_REPORT_NO_TERM) {
        params.noTermOnly = true;
      } else if (exportTermFilter !== 'all') {
        params.termId = exportTermFilter;
      }
      if (academyHasAutomaticPaymentProcessing && exportPaymentStatus !== 'all') {
        params.paymentStatus = exportPaymentStatus as 'pending' | 'paid' | 'refunded';
      }

      const res = await bookingService.exportAcademyBookingCharges(currentAcademy.id, params);
      const rows = res.data ?? [];
      if (rows.length === 0) {
        toast('No bookings match these options for the selected dates.');
        return;
      }
      const slug = (currentAcademy.slug || 'academy').replace(/\s+/g, '-');
      const filename = `booking-charges-${slug}-${exportFrom}-to-${exportTo}.csv`;
      downloadCSV(filename, buildBookingChargesCsvRows(rows));
      toast.success(`Downloaded ${rows.length} row${rows.length === 1 ? '' : 's'}`);
      setExportReportOpen(false);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      toast.error(message || 'Failed to export');
    } finally {
      setExportRunning(false);
    }
  }, [
    currentAcademy,
    canExportCharges,
    exportFrom,
    exportTo,
    exportDateMatch,
    exportBookingStatus,
    exportLessonFilter,
    exportTermFilter,
    exportPaymentStatus,
    academyHasAutomaticPaymentProcessing,
  ]);

  const handleUpdateBookingStatusMany = async (
    bookingIds: string[],
    newStatus: 'pending' | 'confirmed' | 'cancelled' | 'completed'
  ): Promise<boolean> => {
    if (bookingIds.length === 0) return false;
    try {
      await Promise.all(bookingIds.map((id) => bookingService.updateBooking(id, { status: newStatus })));
      toast.success(
        bookingIds.length > 1 ? 'Bookings updated successfully' : 'Booking status updated successfully'
      );
      await fetchBookingsPage(bookingPagination?.page ?? 1);
      return true;
    } catch (error: any) {
      console.error('Error updating booking status:', error);
      toast.error(error.message || 'Failed to update booking status');
      return false;
    }
  };

  const handleCancelBookingMany = async (bookingIds: string[]): Promise<boolean> => {
    if (bookingIds.length === 0) return false;
    try {
      await Promise.all(bookingIds.map((id) => bookingService.cancelBooking(id)));
      toast.success(bookingIds.length > 1 ? 'Bookings cancelled successfully' : 'Booking cancelled successfully');
      await fetchBookingsPage(bookingPagination?.page ?? 1);
      return true;
    } catch (error: any) {
      console.error('Error cancelling booking:', error);
      toast.error(error.message || 'Failed to cancel booking');
      return false;
    }
  };

  // Refund requests for the current academy context (Professional+ only)
  useEffect(() => {
    const loadRefundRequests = async () => {
      if (!currentAcademy?.id || !rebookingRefundsTierResolved || !hasRebookingRefundsAccess) {
        setRefundRequests([]);
        return;
      }
      setRefundRequestsLoading(true);
      try {
        const list = await refundRequestService.getRefundRequests(currentAcademy.id, 'pending');
        setRefundRequests(list);
      } catch (err) {
        console.error('Failed to fetch refund requests:', err);
        setRefundRequests([]);
      } finally {
        setRefundRequestsLoading(false);
      }
    };
    loadRefundRequests();
  }, [currentAcademy?.id, hasRebookingRefundsAccess, rebookingRefundsTierResolved]);

  // Waitlist for the current academy context (Professional+ only)
  useEffect(() => {
    if (
      activeTab !== 'waitlist' ||
      !currentAcademy?.id ||
      !waitlistsTierResolved ||
      !hasWaitlistsAccess
    ) {
      setWaitlistEntries([]);
      setWaitlistLoading(false);
      return;
    }
    const loadWaitlist = async () => {
      setWaitlistLoading(true);
      try {
        const res = await waitlistService.getWaitlistForAcademy(currentAcademy.id);
        setWaitlistEntries(res.data || []);
      } catch {
        toast.error('Failed to load waitlist');
        setWaitlistEntries([]);
      } finally {
        setWaitlistLoading(false);
      }
    };
    loadWaitlist();
  }, [activeTab, currentAcademy?.id, hasWaitlistsAccess, waitlistsTierResolved]);

  const handleRemoveFromWaitlist = async (entryId: string) => {
    try {
      setWaitlistRemovingId(entryId);
      await waitlistService.leaveWaitlist(entryId);
      toast.success('Removed from waitlist');
      setWaitlistEntries((prev) => prev.filter((e) => e.id !== entryId));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to remove');
    } finally {
      setWaitlistRemovingId(null);
    }
  };

  const waitlistByLesson = React.useMemo(() => {
    const grouped: Record<string, WaitlistEntry[]> = {};
    for (const e of waitlistEntries) {
      if (!grouped[e.lessonId]) grouped[e.lessonId] = [];
      grouped[e.lessonId].push(e);
    }
    return grouped;
  }, [waitlistEntries]);

  const STATUS_LABELS: Record<string, string> = {
    pending: 'Waiting',
    notified: 'Notified',
    expired: 'Expired',
    converted: 'Converted',
  };

  // Expand refund requests section when ?refundRequests=true (Professional+ only)
  const showRefundRequestsSection =
    hasRebookingRefundsAccess &&
    (searchParams.get('refundRequests') === 'true' || refundRequests.length > 0);

  const handleProcessRefund = async (req: RefundRequestEnriched) => {
    try {
      await refundRequestService.processRefund(req.id, req.academyId);
      toast.success('Refund processed successfully');
      setRefundRequests((prev) => prev.filter((r) => r.id !== req.id));
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to process refund');
    }
  };

  const handleRejectRefund = async (req: RefundRequestEnriched) => {
    if (!confirm('Reject this refund request?')) return;
    try {
      await refundRequestService.rejectRefund(req.id, req.academyId);
      toast.success('Refund request rejected');
      setRefundRequests((prev) => prev.filter((r) => r.id !== req.id));
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to reject request');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'completed':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'refunded':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  // Determine if a term is current or upcoming
  const isCurrentOrUpcomingTerm = (termId: string | undefined | null): boolean => {
    if (!termId) return false;
    const term = terms[termId];
    if (!term || !term.isActive) return false;

    const now = new Date();
    const startDate = new Date(term.startDate);
    const endDate = new Date(term.endDate);

    // Current: now is between start and end
    const isCurrent = now >= startDate && now <= endDate;
    // Upcoming: start date is in the future
    const isUpcoming = startDate > now;

    return isCurrent || isUpcoming;
  };

  // Filter bookings
  const filteredBookings = bookings.filter(booking => {
    const academyMatch =
      !currentAcademy || booking.academyId === currentAcademy.id;
    const statusMatch = selectedStatusFilter === 'all' || booking.status === selectedStatusFilter;
    const paymentStatusMatch =
      !academyHasAutomaticPaymentProcessing ||
      selectedPaymentStatusFilter === 'all' ||
      booking.paymentStatus === selectedPaymentStatusFilter;
    const studentMatch = selectedStudentFilter === 'all' || booking.studentId === selectedStudentFilter;
    // term + lesson are applied server-side in paginated query

    const q = studentNameSearch.trim().toLowerCase();
    let nameSearchMatch = true;
    if (q) {
      const st = students[booking.studentId];
      if (!st) {
        nameSearchMatch = false;
      } else {
        const full = `${st.firstName} ${st.lastName}`.toLowerCase();
        const first = (st.firstName || '').toLowerCase();
        const last = (st.lastName || '').toLowerCase();
        nameSearchMatch = full.includes(q) || first.includes(q) || last.includes(q);
      }
    }

    return academyMatch && statusMatch && paymentStatusMatch && studentMatch && nameSearchMatch;
  });

  // Sort bookings
  const sortedBookings = React.useMemo(() => {
    const sorted = [...filteredBookings];

    sorted.sort((a, b) => {
      // Default sort: term-priority (current/upcoming first)
      if (sortBy === 'term-priority') {
        const aTermPriority = isCurrentOrUpcomingTerm(a.termId) ? 0 : 1;
        const bTermPriority = isCurrentOrUpcomingTerm(b.termId) ? 0 : 1;

        if (aTermPriority !== bTermPriority) {
          return sortOrder === 'asc' ? aTermPriority - bTermPriority : bTermPriority - aTermPriority;
        }

        // If same priority, sort by term start date
        const aTerm = terms[a.termId];
        const bTerm = terms[b.termId];
        if (aTerm && bTerm) {
          const aStart = new Date(aTerm.startDate).getTime();
          const bStart = new Date(bTerm.startDate).getTime();
          return sortOrder === 'asc' ? aStart - bStart : bStart - aStart;
        }

        // Fallback to created date
        const aCreated = new Date(a.createdAt).getTime();
        const bCreated = new Date(b.createdAt).getTime();
        return sortOrder === 'asc' ? aCreated - bCreated : bCreated - aCreated;
      }

      // Sort by date (createdAt)
      if (sortBy === 'date') {
        const aDate = new Date(a.createdAt).getTime();
        const bDate = new Date(b.createdAt).getTime();
        return sortOrder === 'asc' ? aDate - bDate : bDate - aDate;
      }

      // Sort by student name
      if (sortBy === 'student') {
        const aStudent = students[a.studentId];
        const bStudent = students[b.studentId];
        const aName = aStudent ? `${aStudent.firstName} ${aStudent.lastName}` : 'Unknown';
        const bName = bStudent ? `${bStudent.firstName} ${bStudent.lastName}` : 'Unknown';
        const comparison = aName.localeCompare(bName);
        return sortOrder === 'asc' ? comparison : -comparison;
      }

      // Sort by lesson name
      if (sortBy === 'lesson') {
        const aLesson = lessonById[a.lessonId];
        const bLesson = lessonById[b.lessonId];
        const aName = aLesson?.name || 'Unknown';
        const bName = bLesson?.name || 'Unknown';
        const comparison = aName.localeCompare(bName);
        return sortOrder === 'asc' ? comparison : -comparison;
      }

      // Sort by amount
      if (sortBy === 'amount') {
        return sortOrder === 'asc' ? a.totalAmount - b.totalAmount : b.totalAmount - a.totalAmount;
      }

      // Sort by status
      if (sortBy === 'status') {
        const statusOrder = { 'pending': 0, 'confirmed': 1, 'completed': 2, 'cancelled': 3 };
        const aOrder = statusOrder[a.status] ?? 4;
        const bOrder = statusOrder[b.status] ?? 4;
        return sortOrder === 'asc' ? aOrder - bOrder : bOrder - aOrder;
      }

      return 0;
    });

    return sorted;
  }, [filteredBookings, sortBy, sortOrder, terms, students, lessonById, isCurrentOrUpcomingTerm]);

  /**
   * Per-day camp session rows from the same checkout are clustered (same lesson/student/term/customer,
   * created within a short window). Only the primary booking (earliest createdAt) stays visible.
   */
  const { visibleSortedBookings, campSessionClusterByPrimaryId } = React.useMemo(() => {
    const lessonTypeById = lessonTypeByIdFromMap(lessonById);
    const campBookings = sortedBookings.filter((b) => isCampDailySessionBooking(b, lessonTypeById));
    const { clusterByPrimaryId, hiddenIds } = buildCampSessionClustersWithVisibility(
      campBookings,
      lessonTypeById
    );
    const visibleSortedBookings = sortedBookings.filter((b) => !hiddenIds.has(b.id));
    return {
      visibleSortedBookings,
      campSessionClusterByPrimaryId: clusterByPrimaryId as Map<string, Booking[]>,
    };
  }, [sortedBookings, lessonById]);

  const bookingTableRows = React.useMemo((): BookingEnrichedRow[] => {
    return visibleSortedBookings.map((booking) => {
      const lesson = lessonById[booking.lessonId];
      const academy = academies[booking.academyId];
      const programName = booking.termId ? terms[booking.termId]?.name ?? null : null;
      const lessonTitle = lesson?.name || `Lesson #${booking.lessonId}`;
      const campSessionCluster = campSessionClusterByPrimaryId.get(booking.id);
      const clusterIds = getBookingClusterActionIds(booking, campSessionClusterByPrimaryId);
      const pendingInCluster = clusterIds.pendingInCluster;
      const confirmedInCluster = clusterIds.confirmedInCluster;
      const completedInCluster = clusterIds.completedInCluster;
      const cancellableIds = clusterIds.cancellableIds;
      const student = students[booking.studentId];
      const studentName = student ? `${student.firstName} ${student.lastName}` : 'Unknown';
      return {
        booking,
        lesson,
        academy,
        lessonTitle,
        programName,
        studentName,
        student,
        campSessionCluster,
        pendingInCluster,
        confirmedInCluster,
        completedInCluster,
        cancellableIds,
      };
    });
  }, [
    visibleSortedBookings,
    lessonById,
    terms,
    academies,
    students,
    campSessionClusterByPrimaryId,
    currentAcademy,
  ]);

  const visibleRowIds = React.useMemo(
    () => bookingTableRows.map((r) => r.booking.id),
    [bookingTableRows]
  );

  const allVisibleSelected =
    visibleRowIds.length > 0 && visibleRowIds.every((id) => selectedBookingIds.has(id));
  const someVisibleSelected = visibleRowIds.some((id) => selectedBookingIds.has(id));

  const toggleRow = useCallback((id: string) => {
    setSelectedBookingIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAllVisible = useCallback(() => {
    setSelectedBookingIds((prev) => {
      const ids = bookingTableRows.map((r) => r.booking.id);
      if (ids.length === 0) return prev;
      const allSelected = ids.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) {
        ids.forEach((id) => next.delete(id));
      } else {
        ids.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [bookingTableRows]);

  const bulkConfirmIds = React.useMemo(() => {
    const acc = new Set<string>();
    for (const id of selectedBookingIds) {
      const row = bookingTableRows.find((r) => r.booking.id === id);
      if (row) row.pendingInCluster.forEach((cid) => acc.add(cid));
    }
    return Array.from(acc);
  }, [selectedBookingIds, bookingTableRows]);

  const bulkCancelIds = React.useMemo(() => {
    const acc = new Set<string>();
    for (const id of selectedBookingIds) {
      const row = bookingTableRows.find((r) => r.booking.id === id);
      if (row) row.cancellableIds.forEach((cid) => acc.add(cid));
    }
    return Array.from(acc);
  }, [selectedBookingIds, bookingTableRows]);

  const handleBulkConfirm = async () => {
    if (!hasValidSubscription || bulkConfirmIds.length === 0) return;
    const ok = await handleUpdateBookingStatusMany(bulkConfirmIds, 'confirmed');
    if (ok) setSelectedBookingIds(new Set());
  };

  const handleBulkCancel = async () => {
    if (!hasValidSubscription || bulkCancelIds.length === 0) return;
    const ok = await handleCancelBookingMany(bulkCancelIds);
    if (ok) setSelectedBookingIds(new Set());
  };

  const clearFilters = () => {
    setSelectedStatusFilter('all');
    setSelectedPaymentStatusFilter('all');
    setSelectedStudentFilter('all');
    setSelectedLessonFilter('all');
    setSelectedTermFilter('all');
    setStudentNameSearch('');
    setSortBy('date');
    setSortOrder('desc');
    setSelectedBookingIds(new Set());
  };

  const hasActiveFilters =
    selectedStatusFilter !== 'all' ||
    (academyHasAutomaticPaymentProcessing && selectedPaymentStatusFilter !== 'all') ||
    selectedStudentFilter !== 'all' ||
    selectedLessonFilter !== 'all' ||
    selectedTermFilter !== 'all' ||
    studentNameSearch.trim() !== '';

  const showAcademyName = React.useMemo(
    () => Object.keys(academies).length > 1,
    [academies]
  );

  const detailBooking = React.useMemo(
    () => (detailBookingId ? bookings.find((b) => b.id === detailBookingId) ?? null : null),
    [detailBookingId, bookings]
  );

  const detailCampSessionCluster =
    detailBookingId !== null ? campSessionClusterByPrimaryId.get(detailBookingId) ?? null : null;

  const detailLesson = detailBooking ? lessonById[detailBooking.lessonId] : undefined;
  const detailStudent = detailBooking ? students[detailBooking.studentId] : undefined;
  const detailStudentName = detailStudent
    ? `${detailStudent.firstName} ${detailStudent.lastName}`
    : 'Unknown Student';

  const detailCustomerName = React.useMemo(
    () => (detailBooking ? formatBookingCustomerName(detailBooking) : null),
    [detailBooking]
  );

  const detailBookingActionIds = React.useMemo(() => {
    if (!detailBooking) return null;
    const campSessionCluster = detailCampSessionCluster ?? undefined;
    const booking = detailBooking;
    const pendingInCluster =
      campSessionCluster != null
        ? campSessionCluster.filter((b) => b.status === 'pending').map((b) => b.id)
        : booking.status === 'pending'
          ? [booking.id]
          : [];
    const confirmedInCluster =
      campSessionCluster != null
        ? campSessionCluster.filter((b) => b.status === 'confirmed').map((b) => b.id)
        : booking.status === 'confirmed'
          ? [booking.id]
          : [];
    const completedInCluster =
      campSessionCluster != null
        ? campSessionCluster.filter((b) => b.status === 'completed').map((b) => b.id)
        : booking.status === 'completed'
          ? [booking.id]
          : [];
    const cancellableIds =
      campSessionCluster != null
        ? campSessionCluster
            .filter((b) => b.status === 'pending' || b.status === 'confirmed')
            .map((b) => b.id)
        : booking.status === 'pending' || booking.status === 'confirmed'
          ? [booking.id]
          : [];
    return {
      campSessionCluster,
      pendingInCluster,
      confirmedInCluster,
      completedInCluster,
      cancellableIds,
    };
  }, [detailBooking, detailCampSessionCluster]);

  function bookingActionsCell(row: BookingEnrichedRow) {
    const {
      booking,
      campSessionCluster,
      pendingInCluster,
      confirmedInCluster,
      completedInCluster,
      cancellableIds,
    } = row;
    return (
      <div className="flex justify-end">
        <BookingActionsMenu
          bookingId={booking.id}
          lessonId={booking.lessonId}
          pendingInCluster={pendingInCluster}
          confirmedInCluster={confirmedInCluster}
          completedInCluster={completedInCluster}
          cancellableIds={cancellableIds}
          campSessionClusterSize={campSessionCluster?.length}
          hasValidSubscription={hasValidSubscription}
          subscriptionTooltip={tooltipMessage}
          onViewDetails={setDetailBookingId}
          onUpdateStatus={(ids, status) => handleUpdateBookingStatusMany(ids, status)}
          onCancelBookings={handleCancelBookingMany}
        />
      </div>
    );
  }

  const bookingColumns: PaginatedTableColumn<BookingEnrichedRow>[] = [
    {
      key: 'select',
      header: (
        <Checkbox
          checked={allVisibleSelected ? true : someVisibleSelected ? 'indeterminate' : false}
          onCheckedChange={toggleSelectAllVisible}
          aria-label="Select all bookings on this page"
        />
      ),
      className: 'w-[1%] pl-3 pr-1 align-middle',
      render: (row) => (
        <Checkbox
          checked={selectedBookingIds.has(row.booking.id)}
          onCheckedChange={() => toggleRow(row.booking.id)}
          aria-label={`Select booking: ${row.lessonTitle}`}
        />
      ),
    },
    {
      key: 'booked',
      header: 'Booked',
      className: 'whitespace-nowrap',
      render: (row) => (
        <span className="tabular-nums text-foreground">
          {format(new Date(row.booking.createdAt), 'd MMM yyyy')}
        </span>
      ),
    },
    ...(showAcademyName
      ? ([
          {
            key: 'academy',
            header: 'Academy',
            render: (row: BookingEnrichedRow) => (
              <span className="max-w-[10rem] truncate block">{row.academy?.name ?? '—'}</span>
            ),
          },
        ] as PaginatedTableColumn<BookingEnrichedRow>[])
      : []),
    {
      key: 'lesson',
      header: 'Lesson',
      render: (row) => (
        <div className="min-w-0 max-w-[14rem]">
          <span className="font-medium text-foreground">{row.lessonTitle}</span>
          {row.campSessionCluster && row.campSessionCluster.length > 1 ? (
            <span className="text-muted-foreground font-normal">
              {' '}
              · {row.campSessionCluster.length} sessions
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'student',
      header: 'Student',
      render: (row) => (
        <div className="flex min-w-0 max-w-[12rem] items-center gap-2">
          <UserAvatar firstName={row.student?.firstName} lastName={row.student?.lastName} size="sm" />
          <span className="truncate text-foreground">{row.studentName}</span>
        </div>
      ),
    },
    {
      key: 'program',
      header: 'Program',
      render: (row) => (
        <span className="max-w-[10rem] truncate block">{row.programName ?? '—'}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.campSessionCluster && row.campSessionCluster.length > 1 ? (
            [...new Set(row.campSessionCluster.map((b) => b.status))].map((st) => (
              <span
                key={st}
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${getStatusColor(st)}`}
              >
                {st}
              </span>
            ))
          ) : (
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${getStatusColor(row.booking.status)}`}
            >
              {row.booking.status}
            </span>
          )}
        </div>
      ),
    },
    ...(academyHasAutomaticPaymentProcessing
      ? ([
          {
            key: 'payment',
            header: 'Payment',
            render: (row: BookingEnrichedRow) => (
                <div className="flex flex-wrap gap-1">
                  {row.campSessionCluster && row.campSessionCluster.length > 1 ? (
                    [...new Set(row.campSessionCluster.map((b) => b.paymentStatus))].map((ps) => (
                      <span
                        key={ps}
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${getPaymentStatusColor(ps)}`}
                      >
                        {ps}
                      </span>
                    ))
                  ) : (
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${getPaymentStatusColor(row.booking.paymentStatus)}`}
                    >
                      {row.booking.paymentStatus}
                    </span>
                  )}
                </div>
              ),
          },
        ] as PaginatedTableColumn<BookingEnrichedRow>[])
      : []),
    {
      key: 'amount',
      header: 'Amount',
      className: 'text-right tabular-nums whitespace-nowrap',
      render: (row) => {
        const total =
          row.campSessionCluster && row.campSessionCluster.length > 0
            ? row.campSessionCluster.reduce((s, b) => s + b.totalAmount, 0)
            : row.booking.totalAmount;
        return <span>${total}</span>;
      },
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right w-[1%] pl-2',
      render: (row) => bookingActionsCell(row),
    },
  ];

  const bookingResultsFrom = bookingPagination
    ? (bookingPagination.page - 1) * bookingPagination.limit + 1
    : 1;
  const bookingResultsTo = bookingPagination
    ? Math.min(bookingPagination.page * bookingPagination.limit, bookingPagination.total)
    : bookingTableRows.length;

  const bookingsEmptyHelperText = useMemo(() => {
    if (!hasPaymentProcessingAccess) {
      return 'When families book, enrollments appear here. On Starter, confirm each booking manually after you have collected payment.';
    }
    return 'When families book, enrollments appear here. Enable online payments in Payments so paid bookings confirm automatically.';
  }, [hasPaymentProcessingAccess]);

  const bookingsTabHelp = useMemo(() => getBookingsTabHelp(activeTab), [activeTab]);

  const bookingsTabHelpTour = useMemo(
    () =>
      activeTab === 'bookings' && bookingsTabHelp.tourLabel && bookingsTabHelp.tourDescription
        ? [
            {
              tourKey: BOOKINGS_LIST_TOUR_KEY,
              label: bookingsTabHelp.tourLabel,
              description: bookingsTabHelp.tourDescription,
              onStart: launchBookingsListTour,
            },
          ]
        : [],
    [
      activeTab,
      bookingsTabHelp.tourDescription,
      bookingsTabHelp.tourLabel,
      launchBookingsListTour,
    ],
  );

  if (!user || !hasAcademyManagementRole(user)) {
    return null;
  }

  return (
    <div className="min-h-screen">
      <div className="contents">
        <PageHeader
          title="Bookings"
          help={
            <PageFeatureHelp title={bookingsTabHelp.title} tours={bookingsTabHelpTour}>
              <p>{bookingsTabHelp.description}</p>
            </PageFeatureHelp>
          }
        />

        <div id={BOOKINGS_MANAGEMENT_TOUR_STEP_IDS.BOOKINGS_TABS}>
        <DashboardTabNav<'bookings' | 'waitlist' | 'eoi' | 'private-requests'>
          role="tablist"
          tabs={[
            { id: 'bookings' as const, label: 'Bookings' },
            {
              id: 'waitlist' as const,
              label: 'Waitlist',
              suffix: <TierUnlockBadge unlockTier={getBookingsTabTierUnlockLevel('waitlist')} />,
            },
            {
              id: 'eoi' as const,
              label: 'Expressions of interest',
              mobileLabel: 'EOI',
              suffix: <TierUnlockBadge unlockTier={getBookingsTabTierUnlockLevel('eoi')} />,
            },
            {
              id: 'private-requests' as const,
              label: 'Private requests',
              icon: <UserPlus className="h-4 w-4 shrink-0" aria-hidden />,
              suffix: (
                <TierUnlockBadge unlockTier={getBookingsTabTierUnlockLevel('private-requests')} />
              ),
            },
          ]}
          activeTab={activeTab}
          onChange={setActiveTab}
          ariaLabel="Bookings sections"
        />
        </div>

        {/* Waitlist tab content */}
        {activeTab === 'waitlist' && (
          <FeatureGate
            feature={TierFeatureKey.WAITLISTS}
            mock="waitlists"
            description="Manage lesson waitlists on Professional or Enterprise."
          >
          <div id={BOOKINGS_MANAGEMENT_TOUR_STEP_IDS.WAITLIST} className="space-y-4">
            {waitlistLoading ? (
              <p className="text-gray-600 dark:text-gray-400">Loading waitlist...</p>
            ) : waitlistEntries.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Users className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">No waitlist entries</h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    When guardians join the waitlist for full lessons, they will appear here.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {Object.entries(waitlistByLesson).map(([lessonId, entries]) => {
                  const lesson = lessonById[lessonId];
                  const lessonName = lesson?.name || `Lesson ${lessonId.slice(0, 8)}...`;
                  return (
                    <Card key={lessonId}>
                      <CardContent className="p-4">
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                          {lessonName}
                          <span className="ml-2 text-sm font-normal text-gray-500">
                            ({entries.length} {entries.length === 1 ? 'entry' : 'entries'})
                          </span>
                        </h4>
                        <div className="space-y-2">
                          {entries.map((entry) => {
                            const guardianName = [entry.customerFirstName, entry.customerLastName]
                              .filter(Boolean)
                              .join(' ')
                              .trim();
                            const studentDisplay = [entry.studentFirstName, entry.studentLastName]
                              .filter(Boolean)
                              .join(' ')
                              .trim();
                            const studentName =
                              studentDisplay ||
                              guardianName ||
                              'Student';
                            const contactEmail = entry.customerEmail || '-';
                            const showGuardianDetails =
                              entry.studentIsSelf === false && guardianName.length > 0;

                            return (
                              <div
                                key={entry.id}
                                className="flex items-center justify-between gap-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-800"
                              >
                                <div className="min-w-0 flex-1">
                                  <div className="font-medium text-gray-900 dark:text-white truncate">
                                    {studentName}
                                  </div>
                                  <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                    {contactEmail}
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    Position #{entry.position} • {STATUS_LABELS[entry.status] || entry.status}
                                  </div>
                                  {showGuardianDetails ? (
                                    <details className="mt-2 text-xs text-gray-500 dark:text-gray-400 group">
                                      <summary className="cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-300 list-none [&::-webkit-details-marker]:hidden inline-flex items-center gap-1 text-green-700 dark:text-green-400 font-medium">
                                        <span>More details</span>
                                      </summary>
                                      <p className="mt-2 text-gray-600 dark:text-gray-300">
                                        Parent or guardian: {guardianName}
                                      </p>
                                    </details>
                                  ) : null}
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                                  onClick={() => handleRemoveFromWaitlist(entry.id)}
                                  disabled={waitlistRemovingId === entry.id}
                                  aria-label={`Remove ${studentName} from waitlist`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
          </FeatureGate>
        )}

        {activeTab === 'private-requests' && (
          <FeatureGate
            feature={TierFeatureKey.STUDENT_SELF_SERVICE_BOOKING}
            mock="private-requests"
            description="Guardians request private lesson packages from your public site on Professional or Enterprise."
          >
            <PrivateLessonRequestsPanel
              academyId={currentAcademy?.id}
              academySlug={currentAcademy?.slug}
              canTakeOnlinePayments={
                !!(currentAcademy?.stripeConnectAccountId && currentAcademy?.onboardingComplete)
              }
            />
          </FeatureGate>
        )}

        {activeTab === 'eoi' && (
          <FeatureGate
            feature={TierFeatureKey.PROGRAMS}
            mock="expressions-of-interest"
            description="Review expressions of interest for invitation-only programs on Professional or Enterprise."
          >
            <Card id={BOOKINGS_MANAGEMENT_TOUR_STEP_IDS.EOI_SCOPE} className="mb-6">
              <CardContent className="p-4">
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-3">Lesson scope</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                  Choose a term, then optionally a lesson to filter expressions of interest (EOIs are tied to the lesson families want to book).
                </p>
                <BookingScopeFilterFields
                  selectedTermFilter={selectedTermFilter}
                  onTermFilterChange={setSelectedTermFilter}
                  selectedLessonFilter={selectedLessonFilter}
                  onLessonFilterChange={setSelectedLessonFilter}
                  terms={termsForCurrentAcademy}
                  lessons={lessonsForTermPicker}
                  lessonsLoading={lessonsForTermLoading}
                />
              </CardContent>
            </Card>

            {currentAcademy ? (
              <div id={BOOKINGS_MANAGEMENT_TOUR_STEP_IDS.EOI_LIST}>
                <ExpressionsOfInterestPanel
                  academyId={currentAcademy.id}
                  termIdFilter={selectedTermFilter}
                  lessonIdFilter={selectedLessonFilter}
                />
              </div>
            ) : null}
          </FeatureGate>
        )}

        {/* Bookings tab content */}
        {activeTab === 'bookings' && (
          <>
        {/* Pending Refund Requests */}
        {showRefundRequestsSection && (
          <Card className="mb-6 border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                <h3 className="text-lg font-semibold text-amber-900 dark:text-amber-100">
                  Pending Refund Requests
                </h3>
              </div>
              {refundRequestsLoading ? (
                <p className="text-gray-600 dark:text-gray-400">Loading...</p>
              ) : refundRequests.length === 0 ? (
                <p className="text-gray-600 dark:text-gray-400">
                  No pending refund requests. Students will see a &quot;Request Refund&quot; option when their term lesson is cancelled.
                </p>
              ) : (
                <div className="space-y-3">
                  {refundRequests.map((req) => (
                    <div
                      key={req.id}
                      className="flex flex-wrap items-center justify-between gap-4 p-4 rounded-lg bg-white dark:bg-gray-800 border border-amber-200 dark:border-amber-800"
                    >
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {req.studentName} – {req.lessonName}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {req.booking?.totalAmount != null && req.booking?.currency
                            ? `${req.booking.currency} ${(req.booking.totalAmount / 100).toFixed(2)}`
                            : 'Amount unknown'}
                          {req.reason && ` • ${req.reason}`}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleProcessRefund(req)}
                        >
                          {req.hasPaymentProcessing ? 'Issue Refund' : 'Mark as Refunded'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRejectRefund(req)}
                        >
                          Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {refundRequests.length > 0 && (
                <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                  With payment processing: &quot;Issue Refund&quot; processes the Stripe refund automatically. Without: &quot;Mark as Refunded&quot; records that you&apos;ve completed the refund manually. Reject informs the student their request was declined.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Filters toolbar */}
        <div
          id={BOOKINGS_MANAGEMENT_TOUR_STEP_IDS.BOOKINGS_FILTERS}
          className="flex flex-wrap items-center gap-4 mb-6"
        >
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" aria-hidden />
            <input
              type="search"
              value={studentNameSearch}
              onChange={(e) => setStudentNameSearch(e.target.value)}
              placeholder="Search by student name…"
              aria-label="Search bookings by student name"
              className="w-full pl-10 pr-10 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
            {studentNameSearch ? (
              <button
                type="button"
                onClick={() => setStudentNameSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                aria-label="Clear student name search"
              >
                <X className="w-4 h-4" />
              </button>
            ) : null}
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <Filter className="h-4 w-4" />
                Filters
                {hasActiveFilters && (
                  <span className="ml-1 h-5 min-w-5 rounded-full bg-white/20 px-1.5 flex items-center justify-center text-xs">
                    •
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[min(500px,90vw)] max-h-[80vh] overflow-y-auto p-4" align="start">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 dark:text-white">Filter Bookings</h3>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">Booking scope</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    {`Choose a term, then optionally a lesson. Results load from the server in pages of ${BOOKING_PAGE_SIZE}.`}
                  </p>
                  <BookingScopeFilterFields
                    selectedTermFilter={selectedTermFilter}
                    onTermFilterChange={setSelectedTermFilter}
                    selectedLessonFilter={selectedLessonFilter}
                    onLessonFilterChange={setSelectedLessonFilter}
                    terms={termsForCurrentAcademy}
                    lessons={lessonsForTermPicker}
                    lessonsLoading={lessonsForTermLoading}
                    idPrefix="booking-filter-popover"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
                    <select value={selectedStatusFilter} onChange={(e) => setSelectedStatusFilter(e.target.value)} className="w-full input">
                      <option value="all">All Statuses</option>
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  {academyHasAutomaticPaymentProcessing && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Payment Status</label>
                      <select value={selectedPaymentStatusFilter} onChange={(e) => setSelectedPaymentStatusFilter(e.target.value)} className="w-full input">
                        <option value="all">All</option>
                        <option value="pending">Pending</option>
                        <option value="paid">Paid</option>
                        <option value="refunded">Refunded</option>
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Student</label>
                    <select value={selectedStudentFilter} onChange={(e) => setSelectedStudentFilter(e.target.value)} className="w-full input">
                      <option value="all">All Students</option>
                      {Object.values(students).map((student) => (
                        <option key={student.id} value={student.id}>{student.firstName} {student.lastName}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Sort by</label>
                    <select value={sortBy} onChange={(e) => setSortBy(e.target.value as typeof sortBy)} className="w-full input">
                      <option value="date">Date booked (latest first)</option>
                      <option value="term-priority">Term priority</option>
                      <option value="student">Student Name</option>
                      <option value="lesson">Lesson Name</option>
                      <option value="amount">Amount</option>
                      <option value="status">Status</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Order</label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                      className="w-full flex items-center justify-center space-x-2"
                    >
                      {sortOrder === 'asc' ? <><ArrowUp className="h-4 w-4" /><span>Asc</span></> : <><ArrowDown className="h-4 w-4" /><span>Desc</span></>}
                    </Button>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={clearFilters} className="w-full">
                  Clear Filters
                </Button>
              </div>
            </PopoverContent>
          </Popover>
          <div className="flex flex-wrap items-center gap-4 ml-auto">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {bookingPagination
                ? `${visibleSortedBookings.length} on this page · ${bookingPagination.total} matching`
                : `${visibleSortedBookings.length} booking${visibleSortedBookings.length !== 1 ? 's' : ''}`}
            </span>
            {canExportCharges ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="shrink-0 h-9 w-9 border-border/80 bg-white shadow-sm hover:bg-muted/60 dark:bg-gray-900 dark:hover:bg-gray-800"
                    aria-label="Booking actions"
                  >
                    <MoreVertical className="h-4 w-4" aria-hidden />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[14rem]">
                  <DropdownMenuItem
                    onSelect={() => openExportReportDialog()}
                    className="cursor-pointer gap-2"
                  >
                    <Download className="h-4 w-4 shrink-0" aria-hidden />
                    Export booking report…
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        </div>

        {/* Bookings List — avoid empty-state flash while first paginated page loads */}
        <div id={BOOKINGS_MANAGEMENT_TOUR_STEP_IDS.BOOKINGS_LIST}>
        {bookingListLoading && bookings.length === 0 ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : visibleSortedBookings.length === 0 ? (
          <DashboardEmptyStateCard>
            <DashboardEmptyStateIcon icon={ClipboardList} />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {bookings.length === 0 ? 'No bookings found' : 'No bookings match your filters'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {bookings.length === 0
                ? bookingsEmptyHelperText
                : `Try adjusting your filters. (${bookings.length} total bookings)`}
            </p>
            {bookings.length > 0 && hasActiveFilters && (
              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
            )}
          </DashboardEmptyStateCard>
        ) : (
          <div className="space-y-3">
            {bookingPagination && bookingPagination.total > 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Showing {bookingResultsFrom}–{bookingResultsTo} of {bookingPagination.total} results
              </p>
            )}
            <DashboardDataCard
              toolbar={
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {selectedBookingIds.size > 0
                      ? `${selectedBookingIds.size} selected`
                      : 'Select rows to confirm or cancel in bulk.'}
                  </span>
                  <div className="ml-auto flex flex-wrap items-center gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={!hasValidSubscription || selectedBookingIds.size === 0}
                          className="flex items-center gap-1.5"
                          title={!hasValidSubscription ? tooltipMessage : undefined}
                          aria-label="Bulk actions for selected bookings"
                        >
                          Bulk actions
                          <ChevronDown className="h-4 w-4 opacity-70" aria-hidden />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="min-w-[12rem]">
                        <DropdownMenuItem
                          disabled={!hasValidSubscription || bulkConfirmIds.length === 0}
                          title={
                            !hasValidSubscription
                              ? tooltipMessage
                              : bulkConfirmIds.length === 0 && selectedBookingIds.size > 0
                                ? 'No pending bookings in selection'
                                : undefined
                          }
                          onSelect={() => void handleBulkConfirm()}
                        >
                          Confirm selected
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={!hasValidSubscription || bulkCancelIds.length === 0}
                          title={
                            !hasValidSubscription
                              ? tooltipMessage
                              : bulkCancelIds.length === 0 && selectedBookingIds.size > 0
                                ? 'No pending or confirmed bookings to cancel in selection'
                                : undefined
                          }
                          className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                          onSelect={() => void handleBulkCancel()}
                        >
                          Cancel selected
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              }
            >
                <PaginatedTable<BookingEnrichedRow>
                  columns={bookingColumns}
                  data={bookingTableRows}
                  pagination={bookingPagination}
                  loading={bookingListLoading}
                  emptyMessage="No bookings found."
                  onPageChange={(page) => setBookingPage(page)}
                  getRowKey={(row) => row.booking.id}
                  noWrapperPadding
                />
            </DashboardDataCard>
          </div>
        )}
        </div>

        <Dialog
          open={detailBookingId !== null}
          onOpenChange={(open) => {
            if (!open) setDetailBookingId(null);
          }}
        >
          <DialogContent className="max-h-[85vh] max-w-xl gap-0 overflow-y-auto overflow-x-hidden border bg-background p-0 shadow-2xl sm:max-w-xl sm:rounded-xl">
            {detailBooking && (
              <DialogHeader className="space-y-0 border-b border-border bg-gradient-to-b from-muted/40 to-background px-6 pb-6 pt-6 text-left sm:text-left">
                <DialogTitle className="pr-8 text-xl font-semibold leading-snug tracking-tight">
                  Booking details
                  {detailCampSessionCluster && detailCampSessionCluster.length > 1
                    ? ` · ${detailCampSessionCluster.length} sessions`
                    : ''}
                </DialogTitle>
                <div className="mt-5 flex flex-col divide-y divide-border/70">
                  <BookingDetailDialogRow icon={BookOpen} label="Program">
                    {detailBooking.termId ? terms[detailBooking.termId]?.name ?? '—' : '—'}
                  </BookingDetailDialogRow>
                  <BookingDetailDialogRow icon={BookMarked} label="Lesson">
                    {detailLesson?.name ?? `Lesson #${detailBooking.lessonId}`}
                  </BookingDetailDialogRow>
                  {showAcademyName && academies[detailBooking.academyId]?.name ? (
                    <BookingDetailDialogRow icon={Building2} label="Academy">
                      {academies[detailBooking.academyId]?.name}
                    </BookingDetailDialogRow>
                  ) : null}
                  <BookingDetailDialogRow icon={UserIcon} label="Student">
                    <div className="flex items-center gap-2.5">
                      <UserAvatar
                        firstName={detailStudent?.firstName}
                        lastName={detailStudent?.lastName}
                        size="sm"
                      />
                      <span className="font-medium">{detailStudentName}</span>
                    </div>
                  </BookingDetailDialogRow>
                  <BookingDetailDialogRow icon={Contact} label="Customer">
                    {detailCustomerName ?? '—'}
                  </BookingDetailDialogRow>
                  <BookingDetailDialogRow icon={DollarSign} label="Amount">
                    <span className="font-semibold tabular-nums">
                      $
                      {detailCampSessionCluster && detailCampSessionCluster.length > 0
                        ? detailCampSessionCluster.reduce((s, b) => s + b.totalAmount, 0)
                        : detailBooking.totalAmount}
                    </span>
                  </BookingDetailDialogRow>
                  <BookingDetailDialogRow icon={CalendarDays} label="Booked">
                    <span className="tabular-nums">
                      {format(new Date(detailBooking.createdAt), 'd MMM yyyy, h:mm a')}
                    </span>
                  </BookingDetailDialogRow>
                  {detailLesson ? (
                    <BookingDetailDialogRow icon={Clock} label="Schedule">
                      {renderBookingScheduleContent(
                        detailLesson,
                        detailBooking,
                        getCampSessionDatesForDisplay(
                          detailLesson,
                          detailBooking,
                          detailCampSessionCluster && detailCampSessionCluster.length > 1
                            ? detailCampSessionCluster
                            : undefined
                        )
                      )}
                    </BookingDetailDialogRow>
                  ) : null}
                </div>
              </DialogHeader>
            )}
            {detailBooking && detailBookingActionIds && (
              <DialogFooter className="flex-col items-stretch gap-3 border-t border-border bg-muted/20 px-6 py-4 sm:flex-row sm:flex-wrap sm:justify-end sm:gap-2">
                {!hasValidSubscription ? (
                  <p className="w-full text-left text-xs text-muted-foreground">{tooltipMessage}</p>
                ) : null}
                <div className="flex w-full flex-wrap gap-2 sm:ml-auto sm:w-auto sm:justify-end">
                  {detailBookingActionIds.pendingInCluster.length > 0 && (
                    <Button
                      type="button"
                      variant="default"
                      size="sm"
                      disabled={!hasValidSubscription}
                      onClick={() => {
                        if (hasValidSubscription) {
                          void handleUpdateBookingStatusMany(
                            detailBookingActionIds.pendingInCluster,
                            'confirmed'
                          );
                        }
                      }}
                    >
                      Confirm
                      {detailBookingActionIds.campSessionCluster && detailBookingActionIds.campSessionCluster.length > 1
                        ? ' all pending'
                        : ''}
                    </Button>
                  )}
                  {detailBookingActionIds.confirmedInCluster.length > 0 && (
                    <>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={!hasValidSubscription}
                        onClick={() => {
                          if (hasValidSubscription) {
                            void handleUpdateBookingStatusMany(
                              detailBookingActionIds.confirmedInCluster,
                              'completed'
                            );
                          }
                        }}
                      >
                        Mark Complete
                        {detailBookingActionIds.campSessionCluster &&
                        detailBookingActionIds.campSessionCluster.length > 1
                          ? ' (confirmed)'
                          : ''}
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={!hasValidSubscription}
                        onClick={() => {
                          if (hasValidSubscription) {
                            void handleUpdateBookingStatusMany(
                              detailBookingActionIds.confirmedInCluster,
                              'pending'
                            );
                          }
                        }}
                      >
                        Mark Pending
                        {detailBookingActionIds.campSessionCluster &&
                        detailBookingActionIds.campSessionCluster.length > 1
                          ? ' (confirmed)'
                          : ''}
                      </Button>
                    </>
                  )}
                  {detailBookingActionIds.completedInCluster.length > 0 && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={!hasValidSubscription}
                      onClick={() => {
                        if (hasValidSubscription) {
                          void handleUpdateBookingStatusMany(
                            detailBookingActionIds.completedInCluster,
                            'confirmed'
                          );
                        }
                      }}
                    >
                      Undo Complete
                      {detailBookingActionIds.campSessionCluster &&
                      detailBookingActionIds.campSessionCluster.length > 1
                        ? ' (completed)'
                        : ''}
                    </Button>
                  )}
                  {detailBookingActionIds.cancellableIds.length > 0 && (
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      disabled={!hasValidSubscription}
                      onClick={() => {
                        if (!hasValidSubscription) return;
                        setCancelConfirmIds(detailBookingActionIds.cancellableIds);
                        setCancelConfirmOpen(true);
                      }}
                    >
                      Cancel Booking{detailBookingActionIds.cancellableIds.length > 1 ? 's' : ''}
                    </Button>
                  )}
                </div>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
        </>
        )}
      <Dialog
        open={pastTermsReminderPreview !== null && !pastTermsReminderLoading}
        onOpenChange={(open) => {
          if (!open) dismissPastTermsReminder();
        }}
      >
        <DialogContent className="max-h-[min(88vh,720px)] max-w-xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {pastTermsReminderPreview && pastTermsReminderPreview.terms.length > 1
                ? 'Ended terms still active'
                : 'Term has ended'}
            </DialogTitle>
          </DialogHeader>

          {pastTermsReminderPreview && (
            <>
              <p className="text-sm text-muted-foreground">
                The following {pastTermsReminderPreview.terms.length === 1 ? 'term has' : 'terms have'}{' '}
                passed the end date but {pastTermsReminderPreview.terms.length === 1 ? 'is' : 'are'}{' '}
                still marked active. You can mark all pending and confirmed bookings in{' '}
                {pastTermsReminderPreview.terms.length === 1 ? 'this term' : 'these terms'} completed in
                one step and set {pastTermsReminderPreview.terms.length === 1 ? 'the term' : 'each term'}{' '}
                to inactive.
              </p>

              {pastTermsReminderPreview.terms.reduce((n, t) => n + t.pendingPaymentBookings.length, 0) > 0 ? (
                <Alert variant="warning" className="mt-4">
                  <AlertCircle className="h-4 w-4" aria-hidden />
                  <AlertTitle>Pending payments</AlertTitle>
                  <AlertDescription className="space-y-3">
                    <p>
                      These pending bookings still have unpaid charges (confirmed enrollments with unpaid balances
                      are not listed here). Closing {pastTermsReminderPreview.terms.length === 1 ? 'the term' : 'the terms'}{' '}
                      won’t collect payment automatically—follow up or record payment offline before or after marking
                      bookings complete.
                    </p>
                    <ul className="space-y-4">
                      {pastTermsReminderPreview.terms
                        .filter((t) => t.pendingPaymentBookings.length > 0)
                        .map((term) => (
                          <li key={term.termId}>
                            <p className="mb-1.5 text-xs font-semibold text-foreground dark:text-yellow-100">
                              {term.termName}{' '}
                              <span className="font-normal opacity-90">
                                (ended {format(new Date(term.endDateIso), 'd MMM yyyy')})
                              </span>
                            </p>
                            <ul className="space-y-1.5 rounded-md border border-yellow-800/30 bg-yellow-100/60 p-3 text-xs dark:bg-yellow-950/40 dark:border-yellow-700/40">
                              {term.pendingPaymentBookings.map((b) => (
                                <li
                                  key={b.bookingId}
                                  className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1 text-yellow-950 dark:text-yellow-50"
                                >
                                  <span className="min-w-0 flex-1">
                                    {b.studentName} · {b.lessonName}
                                  </span>
                                  <span className="tabular-nums font-semibold shrink-0">
                                    {formatReminderMoney(b.totalAmount)}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </li>
                        ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              ) : null}

              <ul className="mt-4 space-y-3 border-t border-border pt-4">
                {pastTermsReminderPreview.terms.map((term) => (
                  <li key={term.termId}>
                    <p className="text-sm font-medium text-foreground">
                      {term.termName}{' '}
                      <span className="font-normal text-muted-foreground">
                        (ended {format(new Date(term.endDateIso), 'd MMM yyyy')})
                      </span>
                    </p>
                  </li>
                ))}
              </ul>
            </>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              disabled={pastTermsReminderSubmitting}
              onClick={dismissPastTermsReminder}
            >
              Not now
            </Button>
            <Button
              type="button"
              disabled={pastTermsReminderSubmitting || !hasValidSubscription}
              title={!hasValidSubscription ? tooltipMessage : undefined}
              onClick={() => void confirmPastTermsReminder()}
            >
              {pastTermsReminderSubmitting ? 'Working…' : 'Complete bookings & deactivate term(s)'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={exportReportOpen} onOpenChange={setExportReportOpen}>
        <DialogContent className="max-w-lg max-h-[min(90vh,720px)] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Export booking report</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {academyHasAutomaticPaymentProcessing
              ? 'CSV opens in Excel or Sheets. Dates use UTC. Choose whether the range applies to when the booking was created, or when automatic platform payment processing recorded a successful charge.'
              : 'CSV opens in Excel or Sheets. Dates use UTC and apply to when the booking was created. Turn on automatic payment processing if you also need to filter by when a charge was recorded.'}
          </p>
          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="export-from" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  From
                </label>
                <input
                  id="export-from"
                  type="date"
                  value={exportFrom}
                  onChange={(e) => setExportFrom(e.target.value)}
                  className="w-full input"
                />
              </div>
              <div>
                <label htmlFor="export-to" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  To
                </label>
                <input
                  id="export-to"
                  type="date"
                  value={exportTo}
                  onChange={(e) => setExportTo(e.target.value)}
                  className="w-full input"
                />
              </div>
            </div>
            {academyHasAutomaticPaymentProcessing ? (
              <div>
                <label
                  htmlFor="export-date-match"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                >
                  Match date range on
                </label>
                <select
                  id="export-date-match"
                  value={exportDateMatch}
                  onChange={(e) =>
                    setExportDateMatch(e.target.value as 'booking_created' | 'payment_succeeded')
                  }
                  className="w-full input"
                >
                  <option value="booking_created">Booking created</option>
                  <option value="payment_succeeded">Payment received (platform processing)</option>
                </select>
              </div>
            ) : null}
            <div>
              <label
                htmlFor="export-booking-status"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
              >
                Booking status
              </label>
              <select
                id="export-booking-status"
                value={exportBookingStatus}
                onChange={(e) => setExportBookingStatus(e.target.value)}
                className="w-full input"
              >
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            {academyHasAutomaticPaymentProcessing ? (
              <div>
                <label
                  htmlFor="export-payment-status"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                >
                  Payment status
                </label>
                <select
                  id="export-payment-status"
                  value={exportPaymentStatus}
                  onChange={(e) => setExportPaymentStatus(e.target.value)}
                  className="w-full input"
                >
                  <option value="all">All</option>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="refunded">Refunded</option>
                </select>
              </div>
            ) : null}
            <div>
              <label htmlFor="export-term" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Term
              </label>
              <select
                id="export-term"
                value={exportTermFilter}
                onChange={(e) => setExportTermFilter(e.target.value)}
                className="w-full input"
              >
                <option value="all">All terms</option>
                <option value={EXPORT_BOOKING_REPORT_NO_TERM}>No term (private, camp, standalone)</option>
                {termsForCurrentAcademy.map((term) => (
                  <option key={term.id} value={term.id}>
                    {term.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="export-lesson" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Lesson
              </label>
              <select
                id="export-lesson"
                value={exportLessonFilter}
                onChange={(e) => setExportLessonFilter(e.target.value)}
                disabled={exportModalLessonsLoading}
                className="w-full input disabled:opacity-60"
              >
                <option value="all">
                  {exportModalLessonsLoading ? 'Loading lessons…' : 'All lessons in scope'}
                </option>
                {exportModalLessons.map((lesson) => (
                  <option key={lesson.id} value={lesson.id}>
                    {lesson.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setExportReportOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void handleRunExportReport()}
              disabled={exportRunning || exportModalLessonsLoading}
            >
              {exportRunning ? 'Exporting…' : 'Download CSV'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <CancelBookingConfirmDialog
        open={cancelConfirmOpen}
        onOpenChange={(open) => {
          if (!cancelConfirmSubmitting) {
            setCancelConfirmOpen(open);
            if (!open) setCancelConfirmIds([]);
          }
        }}
        bookingCount={cancelConfirmIds.length}
        loading={cancelConfirmSubmitting}
        onConfirm={async () => {
          setCancelConfirmSubmitting(true);
          try {
            const ok = await handleCancelBookingMany(cancelConfirmIds);
            if (ok) {
              setDetailBookingId(null);
              setCancelConfirmOpen(false);
              setCancelConfirmIds([]);
            }
          } finally {
            setCancelConfirmSubmitting(false);
          }
        }}
      />
      </div>
    </div>
  );
}
